/**
 * Installers — read-only catalog of agent installer artifacts.
 * Backed by:
 *   GET  /v1/installers              → list available installers
 *   POST /v1/installers:download-url → mint a pre-signed download URL
 *
 * Each row corresponds to one (platform, architecture, format,
 * version) combination. Clicking Download mints a fresh pre-signed
 * URL and opens it in a new tab; the URL expires after a short
 * window so we don't cache or persist it.
 *
 * Layout matches the rest of the admin pages — search at top,
 * stats bar, sticky-header table. No invite/create flow since this
 * page is read-only.
 */
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { restGet, restPost } from "../lib/restClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import { useEndpointViewTabs } from "../components/EndpointViewTabs.jsx";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { formatCompact } from "../lib/renderers.js";
import EmptyState from "../components/EmptyState.jsx";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { compareSemver } from "../lib/semver.js";

// ── Schema + enums ───────────────────────────────────────────────

const COLUMNS = [
    { id: "platform",       label: "Platform",     width: 110 },
    { id: "architecture",   label: "Architecture", width: 110 },
    { id: "installerFormat",label: "Format",       width: 90 },
    { id: "version",        label: "Version",      width: 110 },
    { id: "createdAt",      label: "Published",    width: 130 },
    { id: "sizeBytes",      label: "Size",         width: 90 },
    { id: "downloadSha256", label: "SHA256",       width: 160 },
    { id: "_actions",       label: "",             width: 130 },
];

const PAGE_SIZE = 1000;

export const PLATFORM_LABEL = {
    PLATFORM_WINDOWS: "Windows",
    PLATFORM_MACOS:   "macOS",
    PLATFORM_LINUX:   "Linux",
    PLATFORM_UNKNOWN: "Unknown",
};

export const ARCH_LABEL = {
    PROCESSOR_ARCHITECTURE_X86:   "x86",
    PROCESSOR_ARCHITECTURE_X64:   "x64",
    PROCESSOR_ARCHITECTURE_ARM64: "ARM64",
    PROCESSOR_ARCHITECTURE_OTHER: "Other",
    PROCESSOR_ARCHITECTURE_UNKNOWN: "Unknown",
};

export const FORMAT_LABEL = {
    INSTALLER_FORMAT_MSI: "MSI",
    INSTALLER_FORMAT_EXE: "EXE",
    INSTALLER_FORMAT_PKG: "PKG",
};

// ── Row shape ────────────────────────────────────────────────────

export function flattenInstaller(i) {
    const created = i.createdAt ?? i.created_at ?? null;
    const createdDate = created ? new Date(created) : null;
    return {
        version:         i.version ?? "",
        os:              i.os ?? "",
        architecture:    i.architecture ?? "",
        installerFormat: i.installerFormat ?? i.installer_format ?? "",
        manualTesting:   i.manualTesting ?? i.manual_testing ?? false,
        downloadSha256:  i.downloadSha256  ?? i.download_sha256  ?? "",
        // proto declares uint64 — over JSON the gateway emits a string
        // for values that exceed the safe integer range. Keep a Number
        // for sub-PB sizes; fall back to NaN otherwise so the cell
        // renders the dash.
        sizeBytes:       Number(i.sizeBytes ?? i.size_bytes ?? 0),
        createdAt:       Number.isFinite(createdDate?.getTime?.()) ? createdDate : null,
    };
}

// ── Cells ────────────────────────────────────────────────────────

function formatBytes(n) {
    if (!Number.isFinite(n) || n <= 0) return null;
    const units = ["B", "KB", "MB", "GB", "TB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    const decimals = v >= 100 || i === 0 ? 0 : 1;
    return `${v.toFixed(decimals)} ${units[i]}`;
}

// Collapse a flat installer list to the single newest build per
// (platform, architecture, format) — so .msi and .exe for the same OS
// stay distinct rows. Ties on version fall back to the newer publish
// date. This is the default view; "Show all versions" bypasses it.
export function latestPerEnvironment(list) {
    const best = new Map();
    for (const r of list) {
        if (r.manualTesting) continue;
        const key = `${r.os}|${r.architecture}|${r.installerFormat}`;
        const cur = best.get(key);
        if (!cur) { best.set(key, r); continue; }
        const cmp = compareSemver(r.version, cur.version);
        if (cmp > 0) {
            best.set(key, r);
        } else if (cmp === 0) {
            const rt = r.createdAt?.getTime?.() ?? 0;
            const ct = cur.createdAt?.getTime?.() ?? 0;
            if (rt > ct) best.set(key, r);
        }
    }
    return Array.from(best.values());
}

// Collapse duplicate rows that are the SAME artifact published under more
// than one architecture key into a single row labeled "Universal". macOS
// ships a universal2 .pkg (arm64 + x86_64) that the release pipeline
// registers under both the macos/arm64 and macos/x64 keys pointing at one
// identical file — without this it would show as two near-identical rows.
// We only merge when the content hash matches across architectures, so a
// genuine per-arch artifact (or a transient version skew between arches)
// stays split. The first row's concrete architecture is kept as the
// download target; the bytes are identical regardless of which arch key
// we mint the URL for.
export function collapseUniversalRows(list) {
    const groups = new Map();
    const order = [];
    let solo = 0;
    for (const r of list) {
        const key = r.downloadSha256
            ? `${r.os}|${r.installerFormat}|${r.version}|${r.downloadSha256}`
            : `__solo_${solo++}`; // no hash → can't prove identity, never merge
        const g = groups.get(key);
        if (g) {
            g.archs.add(r.architecture);
        } else {
            groups.set(key, { row: r, archs: new Set([r.architecture]) });
            order.push(key);
        }
    }
    return order.map((k) => {
        const { row, archs } = groups.get(k);
        return { ...row, isUniversal: archs.size > 1 };
    });
}

// ── Page ─────────────────────────────────────────────────────────

export default function InstallersPage({ viewDef }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);
    // Default to the newest build per environment; toggle reveals every
    // published version (mirrors the dashboard's installers section).
    const [showAll, setShowAll] = useState(false);

    // Per-row download state. Map<rowKey, "pending" | error>.
    const [dlState, setDlState] = useState({});
    const caps = useMyPermissions();
    const canReadTokens = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_READ);
    const endpointTabs = useEndpointViewTabs({ active: "installers", canReadTokens, canInstallers: true });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        restGet("/v1/installers", { page, page_size: PAGE_SIZE, include_manual_testing: true })
            .then((resp) => {
                if (cancelled) return;
                const list = (resp?.installers ?? []).map(flattenInstaller);
                setRows((prev) => (page === 0 ? list : [...prev, ...list]));
                setTotal(resp?.total ?? list.length);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("[installers]", err);
                setError(err.message || String(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page]);

    const filteredRows = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            if (r.version?.toLowerCase().includes(q)) return true;
            if (PLATFORM_LABEL[r.os]?.toLowerCase().includes(q)) return true;
            if (ARCH_LABEL[r.architecture]?.toLowerCase().includes(q)) return true;
            if (FORMAT_LABEL[r.installerFormat]?.toLowerCase().includes(q)) return true;
            if (r.downloadSha256?.toLowerCase().includes(q)) return true;
            return false;
        });
    }, [rows, deferredSearch]);
    const isFiltering = deferredSearch.trim().length > 0;

    // Default view: newest build per (platform, arch, format), derived
    // from whatever's loaded + matched. "Show all versions" swaps in the
    // full list.
    const latestRows = useMemo(() => latestPerEnvironment(filteredRows), [filteredRows]);
    const displayRows = showAll ? filteredRows : latestRows;
    // Merge same-artifact-under-multiple-arches rows (macOS universal2) into
    // one "Universal" row. Runs after latest/show-all selection so it applies
    // to both views.
    const renderedRows = useMemo(() => collapseUniversalRows(displayRows), [displayRows]);

    const rowKey = useCallback(
        (r) => `${r.os}|${r.architecture}|${r.installerFormat}|${r.version}`,
        [],
    );

    // Click Download → POST to mint a pre-signed URL → open in a
    // new tab. The URL expires in a few minutes so we don't cache;
    // each click mints a fresh one.
    const handleDownload = useCallback(async (r) => {
        const key = rowKey(r);
        setDlState((m) => ({ ...m, [key]: "pending" }));
        try {
            const resp = await restPost("/v1/installers:download-url", {
                version: r.version,
                os: r.os,
                architecture: r.architecture,
                installer_format: r.installerFormat,
            });
            const url = resp?.downloadUrl ?? resp?.download_url ?? "";
            if (!url) throw new Error("Empty download_url in response");
            window.open(url, "_blank", "noopener,noreferrer");
            setDlState((m) => {
                const { [key]: _, ...rest } = m;
                return rest;
            });
        } catch (err) {
            console.error("[installers] download", err);
            setDlState((m) => ({ ...m, [key]: err.message || String(err) }));
        }
    }, [rowKey]);

    const gridCols = useMemo(
        () => `${COLUMNS.map((c) => `${c.width}px`).join(" ")} minmax(0,1fr)`,
        [],
    );
    const minWidth = useMemo(() => COLUMNS.reduce((s, c) => s + c.width, 0), []);

    return (
        <>
            <AdminViewHeader title={viewDef?.label ?? "Installers"} scope="All endpoints" tabs={endpointTabs} />
            <div className="flex flex-1 min-h-0 min-w-0 flex-col py-[8px]">
                <div className="shrink-0 grid gap-[10px] pb-[2px]">
                    <label className="grid gap-[5px]">
                        <span className={NOTE_CLASS}>Search</span>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Platform, architecture, format, version, SHA…"
                            className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                        />
                    </label>

                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{formatCompact(renderedRows.length)}</strong>{" "}
                            {showAll ? (isFiltering ? "matching" : "shown") : "latest"}
                        </span>
                        {showAll && isFiltering ? (
                            <span>
                                <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong> loaded
                            </span>
                        ) : null}
                        {total != null ? (
                            <span>
                                <strong className="text-[color:var(--ink)]">{formatCompact(total)}</strong> total
                            </span>
                        ) : null}
                        {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                        {error ? <span className="text-red-400">{error}</span> : null}
                        <button
                            type="button"
                            onClick={() => setShowAll((v) => !v)}
                            className="ml-auto rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[3px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
                        >
                            {showAll ? "Show latest only" : "Show all versions"}
                        </button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 min-w-0 overflow-auto">
                    <div style={{ minWidth: "100%", width: "max-content" }}>
                        <div
                            className="sticky top-0 z-10 grid items-center gap-x-2 border-b border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[6px] py-[4px] text-[9px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase"
                            style={{ gridTemplateColumns: gridCols }}
                        >
                            {COLUMNS.map((c) => (
                                <span key={c.id} className="truncate">{c.label}</span>
                            ))}
                        </div>

                        {renderedRows.length > 0 ? (
                            <div
                                style={{
                                    opacity: loading ? 0.6 : 1,
                                    transition: "opacity 180ms ease",
                                }}
                            >
                                {renderedRows.map((r) => {
                                    const key = rowKey(r);
                                    const state = dlState[key];
                                    const sizeStr = formatBytes(r.sizeBytes);
                                    const shaShort = r.downloadSha256
                                        ? `${r.downloadSha256.slice(0, 12)}…`
                                        : "";
                                    return (
                                        <div
                                            key={key}
                                            className="grid w-full items-center gap-x-2 border-t border-[color:var(--line-soft)] px-[6px] py-[5px] text-left hover:bg-[color:var(--control-bg)]"
                                            style={{ gridTemplateColumns: gridCols }}
                                        >
                                            <div className="grid min-w-0">
                                                <strong className="truncate text-[11px] leading-[1.08] font-medium">
                                                    {PLATFORM_LABEL[r.os] ?? r.os ?? "—"}
                                                </strong>
                                            </div>
                                            <div className="grid min-w-0">
                                                <span className="truncate text-[11px] leading-[1.08] text-[color:var(--muted)]">
                                                    {r.isUniversal ? "Universal" : (ARCH_LABEL[r.architecture] ?? r.architecture ?? "—")}
                                                </span>
                                            </div>
                                            <div className="grid min-w-0">
                                                <span className="truncate text-[11px] leading-[1.08] text-[color:var(--muted)]">
                                                    {FORMAT_LABEL[r.installerFormat] ?? r.installerFormat ?? "-"}
                                                    {r.manualTesting && <span className="block text-[9px] text-[color:var(--muted)]">Manual testing only</span>}
                                                </span>
                                            </div>
                                            <div className="grid min-w-0">
                                                <strong className="truncate text-[11px] leading-[1.08] font-medium">
                                                    {r.version || "—"}
                                                </strong>
                                            </div>
                                            <div className="grid min-w-0">
                                                {r.createdAt ? (
                                                    <div className="grid gap-0">
                                                        <strong className="text-[10px] leading-[1.12] font-medium">
                                                            {timeLabelFormatter.format(r.createdAt)}
                                                        </strong>
                                                        <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">
                                                            {dayLabelFormatter.format(r.createdAt)}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-[color:var(--muted-soft)]">—</span>
                                                )}
                                            </div>
                                            <div className="grid min-w-0">
                                                <span className="truncate text-[11px] leading-[1.08] text-[color:var(--muted)]">
                                                    {sizeStr ?? "—"}
                                                </span>
                                            </div>
                                            <div className="grid min-w-0">
                                                {shaShort ? (
                                                    <OverflowTooltip
                                                        as="span"
                                                        text={r.downloadSha256}
                                                        className="text-[11px] leading-[1.08] font-mono text-[color:var(--muted)]"
                                                    >
                                                        {shaShort}
                                                    </OverflowTooltip>
                                                ) : (
                                                    <span className="text-[color:var(--muted-soft)]">—</span>
                                                )}
                                            </div>
                                            <div className="grid min-w-0 justify-self-start">
                                                {state === "pending" ? (
                                                    <span className={`${NOTE_CLASS} text-[10px]`}>
                                                        Generating link…
                                                    </span>
                                                ) : typeof state === "string" ? (
                                                    <span className="text-[10px] text-red-400" title={state}>
                                                        Failed
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(r)}
                                                        className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[3px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
                                                    >
                                                        Download
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : loading && rows.length === 0 ? (
                            <div className="grid gap-[6px] py-[12px]">
                                {Array.from({ length: 4 }, (_, i) => (
                                    <div key={i} className="h-[32px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.08 }} />
                                ))}
                            </div>
                        ) : (
                            <EmptyState
                                className="my-[12px]"
                                title={isFiltering ? "No installers match this search" : "No installers available"}
                                note={isFiltering
                                    ? "Clear the search to see the full catalog."
                                    : "Installers become available after an agent build is published."}
                            />
                        )}

                        {showAll && total != null && rows.length < total ? (
                            <div
                                className="grid place-items-center pt-[8px] pb-[10px]"
                                style={{ position: "sticky", left: 0 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={loading}
                                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                                >
                                    Load more · {formatCompact(total - rows.length)} remaining
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
}
